import { IconSearch } from '@myplayer/ui';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  serializeAuditEntriesToCsv,
  subscribeOrganisationAuditLogs,
  verifyAuditChainClient,
} from './audit-chain-client.js';
import { AuditCardsView, AuditTableView } from './audit-log-card.js';
import {
  AuditChainStatusBadge,
  AuditFilterBar,
  buildAuditColumns,
} from './audit-log-filter-bar.js';
import {
  computeAuditFacetCounts,
  filterAndSortAuditEntries,
  getDistinctAuditActions,
  triggerBrowserCsvDownload,
  type AuditActorTypeFilter,
  type AuditEntryRecord,
  type AuditResourceTypeFilter,
  type AuditSortOrder,
} from './audit-log-helpers.js';
import { canUseAllWorkspacesScope, useAuth } from './auth-context.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ToastNotification,
} from './components/ui/primitives.js';
import { RailActionMenu, useInsideAdminShell } from './rail-action-menu.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { ADMIN_STRINGS, formatPlainAdminError } from './strings.js';
import { useResponsiveProductsViewMode } from './use-workspace-identifiers.js';

export interface AuditLogPageProps {
  readonly initialEntries?: readonly AuditEntryRecord[];
  readonly defaultPageSize?: number;
  readonly defaultViewMode?: 'cards' | 'table' | undefined;
  readonly onExportCsv?: (csvContent: string, filename: string) => void;
}

export function AuditLogPage({
  initialEntries,
  defaultViewMode,
  onExportCsv,
}: AuditLogPageProps = {}): ReactElement {
  const { session, can, availableWorkspaces, workspaceScope } = useAuth();
  const insideAdminShell = useInsideAdminShell();
  const orgId = session?.orgId ?? '';
  const workspaceId = session?.activeWorkspaceId ?? '';
  const canReadAuditLog = can('auditLog:read');
  const canExportAuditCsv = canReadAuditLog && can('analytics:export');
  const canViewAllWorkspaces = canUseAllWorkspacesScope(session, availableWorkspaces);
  const defaultWsFilter = workspaceScope === 'ALL' ? 'ALL' : workspaceId;
  const [auditWorkspaceFilter, setAuditWorkspaceFilter] = useState<string>(defaultWsFilter);
  useEffect(() => {
    setAuditWorkspaceFilter(defaultWsFilter);
  }, [defaultWsFilter]);
  const effectiveFilterWorkspaceId = auditWorkspaceFilter === 'ALL' ? '' : auditWorkspaceFilter;
  const isAllWorkspacesView = auditWorkspaceFilter === 'ALL';
  const workspaceNameMap = useMemo<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>(availableWorkspaces.map((ws) => [ws.workspaceId, ws.name]));
    if (workspaceId && !map.has(workspaceId)) map.set(workspaceId, workspaceId);
    return map;
  }, [availableWorkspaces, workspaceId]);

  const [entries, setEntries] = useState<readonly AuditEntryRecord[]>(initialEntries ?? []);
  const [loading, setLoading] = useState<boolean>(
    initialEntries === undefined && canReadAuditLog && Boolean(orgId) && Boolean(workspaceId),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<AuditResourceTypeFilter>('ALL');
  const [actorTypeFilter, setActorTypeFilter] = useState<AuditActorTypeFilter>('ALL');
  const [actionFilter, setActionFilter] = useState<string>('ALL');
  const [sortOrder, setSortOrder] = useState<AuditSortOrder>('NEWEST');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const { viewMode, setViewMode } = useResponsiveProductsViewMode(defaultViewMode);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [exportConfirmation, setExportConfirmation] = useState<string | null>(null);

  useEffect(() => {
    if (initialEntries !== undefined) {
      setEntries(initialEntries);
      setLoading(false);
      return;
    }
    if (!canReadAuditLog || !orgId || !workspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    return subscribeOrganisationAuditLogs({
      orgId,
      workspaceId,
      onEntries: setEntries,
      onError: (err) =>
        setErrorMessage(formatPlainAdminError(err, ADMIN_STRINGS.errors.auditLogFallback)),
      onSettled: () => setLoading(false),
    });
  }, [canReadAuditLog, orgId, workspaceId, initialEntries]);

  const distinctActions = useMemo(
    () => getDistinctAuditActions(entries, effectiveFilterWorkspaceId),
    [entries, effectiveFilterWorkspaceId],
  );
  const facetCounts = useMemo(
    () => computeAuditFacetCounts(entries, effectiveFilterWorkspaceId),
    [entries, effectiveFilterWorkspaceId],
  );

  const filteredEntries = useMemo(
    () =>
      filterAndSortAuditEntries(entries, {
        workspaceId: effectiveFilterWorkspaceId,
        searchQuery,
        resourceTypeFilter,
        actorTypeFilter,
        actionFilter,
        sortOrder,
      }),
    [
      entries,
      effectiveFilterWorkspaceId,
      searchQuery,
      resourceTypeFilter,
      actorTypeFilter,
      actionFilter,
      sortOrder,
    ],
  );
  const chainVerification = useMemo(() => verifyAuditChainClient(entries), [entries]);

  const handleExportCsv = (): void => {
    if (!canExportAuditCsv) return;
    const csvContent = serializeAuditEntriesToCsv(filteredEntries);
    const filename = isAllWorkspacesView
      ? `audit-log-${orgId || 'org'}.csv`
      : `audit-log-${effectiveFilterWorkspaceId || workspaceId || 'org'}.csv`;
    onExportCsv?.(csvContent, filename);
    triggerBrowserCsvDownload(csvContent, filename);
    setExportConfirmation(
      `Exported ${String(filteredEntries.length)} audit ${filteredEntries.length === 1 ? 'entry' : 'entries'} (${filename}) with SHA-256 chain hashes.`,
    );
  };

  const handleExportCsvRef = useRef(handleExportCsv);
  handleExportCsvRef.current = handleExportCsv;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onRailExport = (): void => handleExportCsvRef.current();
    window.addEventListener('mp:export-audit-csv', onRailExport);
    return () => window.removeEventListener('mp:export-audit-csv', onRailExport);
  }, []);

  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(640);
  const auditColumns = useMemo(
    () => buildAuditColumns({ showWorkspaceBadges: isAllWorkspacesView, workspaceNameMap }),
    [isAllWorkspacesView, workspaceNameMap],
  );
  const table = useReactTable({
    data: filteredEntries as AuditEntryRecord[],
    columns: auditColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  const allRows = table.getRowModel().rows;

  if (!canReadAuditLog) {
    return (
      <Card data-testid="audit-log-permission-denied">
        <CardHeader>
          <CardTitle>Audit Log</CardTitle>
          <CardDescription>Access restricted by role</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-[14px] text-fg-secondary">
            {formatCapabilityRestrictionNotice({
              capability: 'auditLog:read',
              actionSummary: 'view or export workspace audit logs',
              callerRole: session?.personaRole,
            })}
          </p>
        </CardContent>
      </Card>
    );
  }
  const hasActiveFilters =
    searchQuery.trim().length > 0 ||
    resourceTypeFilter !== 'ALL' ||
    actorTypeFilter !== 'ALL' ||
    actionFilter !== 'ALL' ||
    (canViewAllWorkspaces && auditWorkspaceFilter !== defaultWsFilter);
  const resetScrollWith =
    <T,>(setter: (val: T) => void) =>
    (val: T): void => {
      setter(val);
      setScrollTop(0);
    };
  const searchActive = showSearchBar || hasActiveFilters;

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      {!insideAdminShell && (
        <RailActionMenu
          pageContext="audit-log"
          canCreateIdentifier={false}
          canExportIdentifier={false}
          canExportAuditLog={canExportAuditCsv}
          onAddProduct={() => undefined}
          onImportCsv={() => undefined}
          onExportCsv={() => undefined}
          onExportAuditCsv={handleExportCsv}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-page-title">Audit Log</h1>
          <span className="text-[12px] font-medium text-fg-secondary tabular-nums">
            {`${isAllWorkspacesView ? 'Organisation' : 'Workspace'} Audit Entries (${filteredEntries.length})`}
          </span>
          <AuditChainStatusBadge verification={chainVerification} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="icon"
            data-testid="toggle-search-btn"
            aria-label="Search and filter audit log"
            aria-expanded={searchActive}
            className={searchActive ? 'border-brand bg-brand-wash text-brand' : undefined}
            onClick={() =>
              setShowSearchBar((prev) => {
                if (prev) {
                  setSearchQuery('');
                  setResourceTypeFilter('ALL');
                  setActorTypeFilter('ALL');
                  setActionFilter('ALL');
                  setAuditWorkspaceFilter(defaultWsFilter);
                  setScrollTop(0);
                }
                return !prev;
              })
            }
          >
            <IconSearch />
          </Button>
        </div>
      </div>

      {!chainVerification.valid && chainVerification.reason !== undefined && (
        <p
          role="alert"
          data-testid="audit-chain-tamper-alert"
          className="rounded-control border border-status-error-border bg-status-error-bg px-3.5 py-2.5 text-[12px] text-status-error"
        >
          <strong>Tamper evidence alert:</strong> {chainVerification.reason}
        </p>
      )}
      <ToastNotification
        data-testid="audit-csv-export-confirmation"
        message={exportConfirmation}
        onDismiss={() => setExportConfirmation(null)}
      />

      <div data-testid="audit-log-table-card" className="space-y-3">
        <div className={searchActive ? 'block' : 'sr-only'}>
          <AuditFilterBar
            searchQuery={searchQuery}
            onSearchChange={resetScrollWith(setSearchQuery)}
            resourceTypeFilter={resourceTypeFilter}
            onResourceTypeChange={resetScrollWith(setResourceTypeFilter)}
            actorTypeFilter={actorTypeFilter}
            onActorTypeChange={resetScrollWith(setActorTypeFilter)}
            actionFilter={actionFilter}
            onActionChange={resetScrollWith(setActionFilter)}
            distinctActions={distinctActions}
            sortOrder={sortOrder}
            onSortOrderChange={(next) => {
              setSortOrder(next);
              setSorting([]);
              setScrollTop(0);
            }}
            viewMode={viewMode}
            onToggleViewMode={(nextMode) => {
              setViewMode(nextMode);
              setScrollTop(0);
            }}
            facetCounts={facetCounts}
            workspaceFilter={auditWorkspaceFilter}
            onWorkspaceFilterChange={resetScrollWith(setAuditWorkspaceFilter)}
            availableWorkspaces={availableWorkspaces}
            showWorkspaceFilter={canViewAllWorkspaces}
            defaultWorkspaceFilter={defaultWsFilter}
          />
        </div>
        {errorMessage !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {errorMessage}
          </p>
        )}
        {viewMode === 'cards' ? (
          <AuditCardsView
            entries={allRows.map((r) => r.original)}
            totalCount={filteredEntries.length}
            loading={loading}
            showWorkspaceBadges={isAllWorkspacesView}
            workspaceNameMap={workspaceNameMap}
            onFilterBySearch={resetScrollWith(setSearchQuery)}
            onFilterByAction={resetScrollWith(setActionFilter)}
          />
        ) : (
          <AuditTableView
            table={table}
            columnCount={auditColumns.length}
            totalCount={filteredEntries.length}
            loading={loading}
            scrollTop={scrollTop}
            viewportHeight={viewportHeight}
            onFilterBySearch={resetScrollWith(setSearchQuery)}
            onFilterByAction={resetScrollWith(setActionFilter)}
            onScrollChange={(nextTop, nextHeight) => {
              setScrollTop(nextTop);
              if (nextHeight > 0) setViewportHeight(nextHeight);
            }}
          />
        )}
      </div>
    </div>
  );
}
